<template>
    <div class="min-h-screen text-white relative overflow-hidden" style="background: #06020e;">

        <!-- Aurora orbs -->
        <div class="absolute inset-0 pointer-events-none overflow-hidden">
            <div class="aurora aurora-1"></div>
            <div class="aurora aurora-2"></div>
            <div class="aurora aurora-3"></div>
            <div class="aurora aurora-4"></div>
            <div class="aurora aurora-5"></div>
        </div>

        <!-- Hex grid overlay -->
        <div class="absolute inset-0 pointer-events-none" style="opacity:0.05;">
            <svg class="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <pattern id="hexGrid" width="52" height="90" patternUnits="userSpaceOnUse">
                        <polygon points="26,0 52,15 52,45 26,60 0,45 0,15"
                                 fill="none" stroke="#c084fc" stroke-width="0.9"/>
                        <polygon points="0,45 26,60 26,90 0,105 -26,90 -26,60"
                                 fill="none" stroke="#c084fc" stroke-width="0.9"/>
                        <polygon points="52,45 78,60 78,90 52,105 26,90 26,60"
                                 fill="none" stroke="#c084fc" stroke-width="0.9"/>
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#hexGrid)"/>
            </svg>
        </div>

        <!-- Mesh gradient tint -->
        <div class="absolute inset-0 pointer-events-none"
             style="background: linear-gradient(135deg,rgba(124,58,237,0.07) 0%,rgba(232,121,249,0.04) 40%,rgba(6,182,212,0.04) 70%,rgba(99,102,241,0.07) 100%)"></div>

        <!-- Blockchain network canvas -->
        <canvas ref="bgCanvas" class="absolute inset-0 w-full h-full pointer-events-none" style="opacity:0.7;"></canvas>

        <!-- Edge vignette -->
        <div class="absolute inset-0 pointer-events-none"
             style="background:radial-gradient(ellipse 110% 90% at 50% 50%,transparent 30%,rgba(6,2,14,0.35) 65%,rgba(6,2,14,0.80) 100%)"></div>

        <Navbar/>
        <slot/>
        <WalletModal
            :show="showWalletModal"
            :registration-enabled="page.props.registrationEnabled !== false"
            :manual-auth-enabled="page.props.manualAuthEnabled !== false"
            @close="closeModal"
            @success="handleWalletSuccess"
        />
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { usePage } from '@inertiajs/vue3'
import Navbar from '@/Components/Frontend/Navbar.vue'
import WalletModal from '@/Components/Frontend/WalletModal.vue'
import { useWalletModal } from '@/composables/useWalletModal'

const page = usePage()
const { showWalletModal, closeModal } = useWalletModal()

const handleWalletSuccess = (user) => {
    closeModal()
}

const bgCanvas = ref(null)
let animId = null
let cleanupResize = null

const COLORS = ['#a75ded','#e879f9','#06b6d4','#818cf8','#c084fc','#7c3aed','#f472b6']

const initCanvas = () => {
    const canvas = bgCanvas.value
    if (!canvas) return

    const setSize = () => {
        canvas.width  = window.innerWidth
        canvas.height = window.innerHeight
    }
    setSize()

    const ctx = canvas.getContext('2d')

    const nodeCount = Math.min(55, Math.max(22, Math.floor(window.innerWidth / 28)))
    const nodes = Array.from({ length: nodeCount }, () => ({
        x:      Math.random() * canvas.width,
        y:      Math.random() * canvas.height,
        vx:     (Math.random() - 0.5) * 0.22,
        vy:     (Math.random() - 0.5) * 0.22,
        r:      Math.random() * 2.2 + 1.2,
        color:  COLORS[Math.floor(Math.random() * COLORS.length)],
        phase:  Math.random() * Math.PI * 2,
        isHub:  Math.random() > 0.74,
    }))

    const packets = []

    const spawnPkt = (fi, ti) => {
        if (packets.length > 90) return
        packets.push({
            fi, ti,
            t:     0,
            speed: Math.random() * 0.0045 + 0.0018,
            color: COLORS[Math.floor(Math.random() * COLORS.length)],
            sz:    Math.random() * 1.6 + 0.9,
        })
    }

    const tick = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height)

        const MAX_D = Math.min(canvas.width, canvas.height) * 0.25

        // Move nodes
        for (const n of nodes) {
            n.x += n.vx; n.y += n.vy; n.phase += 0.013
            if (n.x < -100) n.x = canvas.width  + 100
            else if (n.x > canvas.width  + 100) n.x = -100
            if (n.y < -100) n.y = canvas.height + 100
            else if (n.y > canvas.height + 100) n.y = -100
        }

        // Connections
        ctx.lineWidth = 0.65
        for (let i = 0; i < nodes.length; i++) {
            for (let j = i + 1; j < nodes.length; j++) {
                const dx = nodes[i].x - nodes[j].x
                const dy = nodes[i].y - nodes[j].y
                const d  = Math.sqrt(dx * dx + dy * dy)
                if (d > MAX_D) continue
                const a = (1 - d / MAX_D) * 0.18
                ctx.beginPath()
                ctx.moveTo(nodes[i].x, nodes[i].y)
                ctx.lineTo(nodes[j].x, nodes[j].y)
                ctx.strokeStyle = `rgba(167,93,237,${a})`
                ctx.stroke()
                if (Math.random() < 0.00028) spawnPkt(i, j)
            }
        }

        // Packets
        for (let i = packets.length - 1; i >= 0; i--) {
            const p = packets[i]
            p.t += p.speed
            if (p.t >= 1) { packets.splice(i, 1); continue }
            const a = nodes[p.fi], b = nodes[p.ti]
            const x = a.x + (b.x - a.x) * p.t
            const y = a.y + (b.y - a.y) * p.t
            ctx.beginPath()
            ctx.arc(x, y, p.sz, 0, Math.PI * 2)
            ctx.fillStyle   = p.color
            ctx.shadowColor = p.color
            ctx.shadowBlur  = 12
            ctx.fill()
            ctx.shadowBlur = 0
        }

        // Nodes
        for (const n of nodes) {
            const pulse = Math.sin(n.phase) * 0.5 + 0.5

            if (n.isHub) {
                ctx.beginPath()
                ctx.arc(n.x, n.y, n.r * 6 + pulse * 4, 0, Math.PI * 2)
                ctx.fillStyle = n.color + '0e'
                ctx.fill()
            }

            ctx.beginPath()
            ctx.arc(n.x, n.y, n.r * (2 + pulse * 0.9), 0, Math.PI * 2)
            ctx.fillStyle = n.color + '1a'
            ctx.fill()

            ctx.beginPath()
            ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2)
            ctx.fillStyle   = n.color
            ctx.shadowColor = n.color
            ctx.shadowBlur  = 10 + pulse * 10
            ctx.fill()
            ctx.shadowBlur = 0
        }

        animId = requestAnimationFrame(tick)
    }

    tick()

    const onResize = () => setSize()
    window.addEventListener('resize', onResize)
    cleanupResize = () => window.removeEventListener('resize', onResize)
}

onMounted(()  => initCanvas())
onUnmounted(() => {
    if (animId)       cancelAnimationFrame(animId)
    if (cleanupResize) cleanupResize()
})
</script>

<style scoped>
.aurora {
    position: absolute;
    border-radius: 50%;
    filter: blur(110px);
    will-change: transform;
}
.aurora-1 {
    width: 820px; height: 720px;
    top: -18%; right: -6%;
    background: radial-gradient(circle, rgba(124,58,237,0.52) 0%, rgba(167,93,237,0.26) 42%, transparent 70%);
    animation: aur1 28s ease-in-out infinite;
}
.aurora-2 {
    width: 720px; height: 660px;
    bottom: -14%; left: -5%;
    background: radial-gradient(circle, rgba(232,121,249,0.46) 0%, rgba(217,70,239,0.23) 44%, transparent 70%);
    animation: aur2 34s ease-in-out infinite;
}
.aurora-3 {
    width: 560px; height: 520px;
    top: 38%; left: 30%;
    background: radial-gradient(circle, rgba(6,182,212,0.32) 0%, rgba(14,116,144,0.16) 50%, transparent 70%);
    animation: aur3 40s ease-in-out infinite;
}
.aurora-4 {
    width: 500px; height: 480px;
    top: 12%; left: -9%;
    background: radial-gradient(circle, rgba(99,102,241,0.36) 0%, rgba(79,70,229,0.18) 50%, transparent 70%);
    animation: aur4 24s ease-in-out infinite;
}
.aurora-5 {
    width: 440px; height: 420px;
    bottom: 18%; right: 8%;
    background: radial-gradient(circle, rgba(244,114,182,0.32) 0%, rgba(157,23,77,0.16) 50%, transparent 70%);
    animation: aur5 31s ease-in-out infinite;
}

@keyframes aur1 {
    0%,100% { transform: translate(0,0) scale(1); }
    25%  { transform: translate(-55px, 38px) scale(1.07); }
    50%  { transform: translate(28px,-48px) scale(0.93); }
    75%  { transform: translate(-22px, 18px) scale(1.02); }
}
@keyframes aur2 {
    0%,100% { transform: translate(0,0) scale(1); }
    30%  { transform: translate(68px,-28px) scale(1.09); }
    60%  { transform: translate(-18px, 58px) scale(0.92); }
    80%  { transform: translate(28px,-12px) scale(1.03); }
}
@keyframes aur3 {
    0%,100% { transform: translate(0,0) scale(1); }
    20%  { transform: translate(-38px,-58px) scale(1.05); }
    55%  { transform: translate(48px, 28px) scale(0.96); }
    80%  { transform: translate(-14px, 14px) scale(1.02); }
}
@keyframes aur4 {
    0%,100% { transform: translate(0,0) scale(1); }
    45%  { transform: translate(38px, 72px) scale(1.11); }
    75%  { transform: translate(-18px,-28px) scale(0.95); }
}
@keyframes aur5 {
    0%,100% { transform: translate(0,0) scale(1); }
    35%  { transform: translate(-28px,-42px) scale(1.06); }
    65%  { transform: translate(20px, 48px) scale(0.95); }
}

@media (prefers-reduced-motion: reduce) {
    .aurora { animation: none !important; }
}
</style>
